const { format } = require('date-fns')
const { PGAdapter } = require('../../common')

const TABLE = 'avocado.data_supply_plan_params'
const COLUMNS = [
  'id',
  'organisation_id',
  'date',
  'program',
  'sku',
  'location_type',
  'supply_period_days',
  'lead_time_days',
  'created_at'
]

// data_productsku.old_sku_id (the FK target) stores raw skus like '5056', without
// the 'product:' prefix used everywhere in the FS app. Strip on write/query, add
// it back on read so callers stay on the canonical 'product:xxx' form.
const stripProductPrefix = (sku) =>
  typeof sku === 'string' && sku.startsWith('product:')
    ? sku.replace('product:', '')
    : sku

const rowToDomain = (row) => row && {
  id: row.id,
  date: row.date instanceof Date ? format(row.date, 'YYYY-MM-DD') : row.date,
  program: row.program_fsid,
  sku: row.sku ? `product:${row.sku}` : row.sku,
  locationType: row.location_type,
  supplyPeriodDays: Number(row.supply_period_days),
  leadTimeDays: Number(row.lead_time_days),
  createdAt: row.created_at
}

class SupplyPlanPgAdapter extends PGAdapter {
  constructor (pgConnection, user, logger) {
    super(
      pgConnection,
      TABLE,
      user,
      COLUMNS,
      'id',
      {},
      logger,
      { strictColumns: true, addUuid: true, securedByOrganisationId: true }
    )
  }

  async resolveProgramUuids (programFsids) {
    if (!programFsids.length) return {}
    const unique = [...new Set(programFsids)]
    const { rows } = await this.pgConnection.query(
      'SELECT id, fsid FROM avocado.data_program WHERE fsid = ANY($1)',
      [unique]
    )
    const byFsid = Object.fromEntries(rows.map(r => [r.fsid, r.id]))
    for (const fsid of unique) {
      if (!byFsid[fsid]) {
        const err = new Error(`Program not found: ${fsid}`)
        err.status = 400
        throw err
      }
    }
    return byFsid
  }

  async validateSkusExist (skus) {
    if (!skus.length) return
    const unique = [...new Set(skus)]
    const { rows } = await this.pgConnection.query(
      'SELECT old_sku_id FROM avocado.data_productsku WHERE old_sku_id = ANY($1)',
      [unique]
    )
    const found = new Set(rows.map(r => r.old_sku_id))
    const missing = unique.filter(sku => !found.has(sku))
    if (missing.length) {
      const err = new Error(`Product(s) not found: ${missing.join(', ')}`)
      err.status = 400
      throw err
    }
  }

  async getLatest ({ programFsid, sku, locationType, date }) {
    const { [programFsid]: programId } = await this.resolveProgramUuids([programFsid])
    const params = [programId, stripProductPrefix(sku), locationType]
    let dateClause = ''
    if (date) {
      params.push(date)
      dateClause = `AND sp.date <= $${params.length}`
    }
    const { rows } = await this.pgConnection.query(
      `SELECT sp.*, p.fsid AS program_fsid
       FROM ${this.tableName} sp
       JOIN avocado.data_program p ON sp.program = p.id
       WHERE sp.program = $1
         AND sp.sku = $2
         AND sp.location_type = $3
         ${dateClause}
         AND sp.organisation_id = $${params.length + 1}
       ORDER BY sp.date DESC, sp.created_at DESC
       LIMIT 1`,
      [...params, this.userOrganisation]
    )
    return rowToDomain(rows[0])
  }

  async listLatest ({ programFsid, date }) {
    const { [programFsid]: programId } = await this.resolveProgramUuids([programFsid])
    const params = [programId, this.userOrganisation]
    let dateClause = ''
    if (date) {
      params.push(date)
      dateClause = `AND sp.date <= $${params.length}`
    }
    const { rows } = await this.pgConnection.query(
      `SELECT DISTINCT ON (sp.sku, sp.location_type)
         sp.*, p.fsid AS program_fsid
       FROM ${this.tableName} sp
       JOIN avocado.data_program p ON sp.program = p.id
       WHERE sp.program = $1
         AND sp.organisation_id = $2
         ${dateClause}
       ORDER BY sp.sku, sp.location_type, sp.date DESC, sp.created_at DESC`,
      params
    )
    return rows.map(rowToDomain)
  }

  async insertMany (rows) {
    const cleanedSkus = rows.map(r => stripProductPrefix(r.sku))
    const [programIds] = await Promise.all([
      this.resolveProgramUuids(rows.map(r => r.program)),
      this.validateSkusExist(cleanedSkus)
    ])
    const dbRows = rows.map((row, i) => ({
      program: programIds[row.program],
      sku: cleanedSkus[i],
      location_type: row.locationType,
      supply_period_days: row.supplyPeriodDays,
      lead_time_days: row.leadTimeDays,
      date: row.date
    }))
    const inserted = await this.bulkCreate(dbRows)
    const fsidByUuid = Object.fromEntries(
      Object.entries(programIds).map(([fsid, id]) => [id, fsid])
    )
    return inserted.map(r => rowToDomain({ ...r, program_fsid: fsidByUuid[r.program] }))
  }
}

module.exports = SupplyPlanPgAdapter
